// The invite landing page: who invited you to what, then sign up, sign in, or (already signed in) join.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { InvitePreview } from "@bloom/shared";
import { api } from "../api.ts";
import { useSession } from "../session.tsx";
import { AuthCard, initials } from "../components/ui.tsx";
import { SignUpForm } from "./Auth.tsx";

const REASONS: Record<string, string> = {
  expired: "This invite has expired.",
  used_up: "This invite has been used up.",
  revoked: "This invite was turned off.",
  not_found: "This invite doesn't exist.",
  closed: "This server isn't taking new members right now.",
  banned: "You can't join this server.",
};

export function Invite() {
  const { code = "" } = useParams();
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<InvitePreview>(`/invites/${code}`).then(setPreview, (e: Error) => setError(e.message));
  }, [code]);

  if (error) return <AuthCard><p className="error">{error}</p></AuthCard>;
  if (!preview || me === undefined) return <AuthCard><p className="muted">Loading invite…</p></AuthCard>;
  if (!preview.valid || !preview.server) {
    return (
      <AuthCard>
        <h1>{REASONS[preview.reason ?? "not_found"]}</h1>
        {preview.reason !== "banned" && (
          <p style={{ margin: 0 }}>{preview.reason === "closed" ? "Ask whoever sent it to let you know when it opens up again." : "Ask whoever sent it for a new link."}</p>
        )}
      </AuthCard>
    );
  }
  const s = preview.server;
  const already = me?.servers.some((x) => x.id === s.id);
  const join = async () => {
    setBusy(true);
    try {
      const r = await api<{ serverId: string }>(`/invites/${code}/accept`, { body: {} });
      await refresh();
      navigate(`/s/${r.serverId}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthCard>
      <div className="row" style={{ gap: 14 }}>
        <div className="server-badge" style={{ background: s.color }} aria-hidden="true">{initials(s.name)}</div>
        <div>
          <div className="muted" style={{ fontSize: 14 }}>{preview.invitedBy ?? "Someone"} invited you to</div>
          <h1>{s.name}</h1>
          <div className="muted" style={{ fontSize: 13 }}>{s.memberCount} {s.memberCount === 1 ? "member" : "members"}</div>
        </div>
      </div>
      {me ? (
        already ? (
          <Link className="btn btn-primary" to={`/s/${s.id}`}>You're already in. Open {s.name}</Link>
        ) : (
          <button className="btn btn-primary" onClick={join} disabled={busy}>Join {s.name}</button>
        )
      ) : (
        <>
          <SignUpForm inviteCode={code} onDone={(serverId) => navigate(serverId ? `/s/${serverId}` : "/", { replace: true })} />
          <p style={{ margin: 0, fontSize: 14 }}>
            Already have an account? <Link to={`/login?next=${encodeURIComponent(`/invite/${code}`)}`}>Sign in to join</Link>
          </p>
        </>
      )}
    </AuthCard>
  );
}
